import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DevToolsService } from '../dev-tools.service';
import {
  findMatchingPayment,
  isPaymentTriggerTask,
  mergeTasksWithPayments,
} from '../../../features/workspace/admin/pages/demarches-page/demarches-page.model';
import { TaskRecordDto } from '../../models/guest/requests.dto';

/* ----------------------------------------------------------------------------
 * Types locaux
 * ------------------------------------------------------------------------- */

interface AnyPayment {
  id?: string;
  _id?: string;
  paymentId?: string;
  transactionId?: string;
  status?: string;
  amount?: any;
  currency?: string;
  createdAt?: string | number | Date;
  updatedAt?: string | number | Date;
  providerName?: string;
  gateway?: string;
  [k: string]: any;
}

interface TimelineItem {
  kind: 'task' | 'payment';
  id: string;
  label: string;
  start: number;
  end: number;
  leftPct: number;
  widthPct: number;
  status?: string;
  isTrigger?: boolean;
  matchedWith?: string | null;
  raw: any;
  colorClass: string;
}

interface MatchRow {
  taskId: string;
  taskName: string;
  createdAt: number;
  isTrigger: boolean;
  matchedPaymentId: string | null;
  matchedPaymentTime: number | null;
  deltaMs: number | null;
  deltaClass: string;
  enrichment: boolean;
  synthetic: boolean;
  resolvedAmount: number;
  amountSource: string;
  currency: string;
  raw: TaskRecordDto;
}

interface CrossMatch {
  p1: AnyPayment;
  p2: AnyPayment;
  spanMs: number;
  suspiciousTasks: Array<{ id: string; name: string; at: number }>;
}

interface AmountStep {
  label: string;
  value: string;
  picked: boolean;
  skipped: boolean;
}

/* ----------------------------------------------------------------------------
 * Helpers (pure)
 * ------------------------------------------------------------------------- */

function parseTime(v: any): number {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function pid(p: AnyPayment): string {
  return String(p?.id || p?._id || p?.paymentId || p?.transactionId || '');
}

function fmtTime(ts: number): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(ts);
  }
}

function fmtDate(ts: number): string {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('fr-FR');
  } catch {
    return String(ts);
  }
}

function fmtDelta(ms: number | null): string {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  const sign = s > 0 ? '+' : '';
  if (Math.abs(s) < 60) return `${sign}${s}s`;
  const m = Math.round(s / 60);
  return `${sign}${m}m`;
}

function deltaClassFor(ms: number | null): string {
  if (ms == null) return 'tw-text-error-500';
  const abs = Math.abs(ms);
  if (abs < 10_000) return 'tw-text-success-500';
  if (abs < 60_000) return 'tw-text-warning-500';
  return 'tw-text-error-500';
}

function resolveAmount(
  task: TaskRecordDto,
  demarcheAmount: number
): { amount: number; source: string; steps: AmountStep[] } {
  const steps: AmountStep[] = [];
  const anyTask = task as any;

  const v1 = anyTask?.taskInvocationData?.calculatedCosts?.total;
  const v2 = anyTask?.formSetting?.result?.calculatedCosts?.total;
  const v3raw = task.parameters?.['gnspdPaymentAmount'];
  const v3 = v3raw != null ? parseFloat(String(v3raw)) : NaN;
  const v4 = demarcheAmount;

  let picked = -1;
  if (typeof v1 === 'number' && v1 > 0) picked = 0;
  else if (typeof v2 === 'number' && v2 > 0) picked = 1;
  else if (!isNaN(v3) && v3 > 0) picked = 2;
  else if (typeof v4 === 'number' && v4 > 0) picked = 3;

  steps.push({
    label: 'task.taskInvocationData?.calculatedCosts?.total',
    value: v1 == null ? 'undefined' : String(v1),
    picked: picked === 0,
    skipped: picked !== -1 && picked < 0,
  });
  steps.push({
    label: 'task.formSetting?.result?.calculatedCosts?.total',
    value: v2 == null ? 'undefined' : String(v2),
    picked: picked === 1,
    skipped: picked !== -1 && picked < 1,
  });
  steps.push({
    label: 'parseFloat(task.parameters?.gnspdPaymentAmount)',
    value: v3raw == null ? 'undefined' : `${v3raw} → ${isNaN(v3) ? 'NaN' : v3}`,
    picked: picked === 2,
    skipped: picked !== -1 && picked < 2,
  });
  steps.push({
    label: 'demarche.amount (fallback)',
    value: v4 == null ? 'undefined' : String(v4),
    picked: picked === 3,
    skipped: picked !== -1 && picked < 3,
  });

  const values = [v1, v2, isNaN(v3) ? undefined : v3, v4];
  const sources = [
    'taskInvocationData.calculatedCosts',
    'formSetting.result.calculatedCosts',
    'parameters.gnspdPaymentAmount',
    'demarche.amount',
  ];
  const amount = picked >= 0 ? (values[picked] as number) : 0;
  const source = picked >= 0 ? sources[picked] : 'default (0)';
  return { amount, source, steps };
}

/* ----------------------------------------------------------------------------
 * Composant
 * ------------------------------------------------------------------------- */

@Component({
  selector: 'dev-payment-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="tw-h-full tw-flex tw-flex-col tw-text-[11px] tw-text-gray-200 tw-font-sans">
      <!-- Toolbar -->
      <div class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-border-b tw-border-gray-800 tw-bg-gray-900/60">
        <span class="ph ph-credit-card tw-text-success-500"></span>
        <span class="tw-font-semibold">Payment Matcher Inspector</span>
        <span class="tw-text-gray-500">·</span>
        <span class="tw-text-gray-400">{{ tasks().length }} tasks / {{ payments().length }} payments</span>
        <span class="tw-flex-1"></span>
        <label class="tw-flex tw-items-center tw-gap-1 tw-text-gray-400 tw-cursor-pointer tw-select-none">
          <input type="checkbox" [checked]="hideNonTrigger()" (change)="hideNonTrigger.set($any($event.target).checked)" />
          Hide non-trigger tasks
        </label>
        <button
          type="button"
          class="tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-border tw-border-gray-700"
          (click)="exportCsv()"
          title="Export CSV du matching table"
        >
          <span class="ph ph-download-simple"></span> CSV
        </button>
        <button
          type="button"
          class="tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-800 hover:tw-bg-gray-700 tw-border tw-border-gray-700"
          (click)="refresh()"
          title="Recharger depuis le snapshot"
        >
          <span class="ph ph-arrows-clockwise"></span>
        </button>
      </div>

      @if (!hasData()) {
        <div class="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-text-gray-500 tw-text-xs tw-p-4 tw-text-center">
          Aucune donnée de paiement exposée par le snapshot.<br />
          Ouvre une démarche dans la page admin pour alimenter le DevTools.
        </div>
      } @else {
        <div class="tw-flex-1 tw-overflow-auto tw-p-2 tw-space-y-4">
          <!-- Section 1 : Timeline -->
          <section class="tw-border tw-border-gray-800 tw-rounded tw-bg-gray-900/40">
            <header class="tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-gray-800">
              <span class="ph ph-timer tw-text-success-500"></span>
              <span class="tw-font-semibold">Matching Timeline</span>
              <span class="tw-text-gray-500 tw-text-[10px]">
                {{ fmtDate(timelineStart()) }} → {{ fmtDate(timelineEnd()) }}
              </span>
              <span class="tw-flex-1"></span>
              <span class="tw-text-gray-400">zoom</span>
              <input
                type="range"
                min="1"
                max="8"
                step="0.5"
                [value]="zoom()"
                (input)="zoom.set(+$any($event.target).value)"
                class="tw-w-24"
              />
              <span class="tw-w-8 tw-text-right">{{ zoom() }}x</span>
            </header>

            <div class="tw-overflow-x-auto">
              <div
                class="tw-relative"
                [style.min-width.%]="100 * zoom()"
                [style.padding.px]="8"
              >
                <!-- Axe temps -->
                <div class="tw-relative tw-h-5 tw-border-b tw-border-gray-700/50 tw-mb-1">
                  @for (tick of timeTicks(); track tick.pct) {
                    <div
                      class="tw-absolute tw-top-0 tw-h-full tw-text-[9px] tw-text-gray-500 tw-border-l tw-border-gray-800"
                      [style.left.%]="tick.pct"
                    >
                      <span class="tw-pl-0.5">{{ tick.label }}</span>
                    </div>
                  }
                </div>

                <!-- TASKS row -->
                <div class="tw-relative tw-h-6 tw-mb-1">
                  <span class="tw-absolute tw-left-0 tw--translate-x-full tw-pr-2 tw-text-gray-500 tw-text-[10px] tw-leading-6">TASKS</span>
                  @for (item of timelineTasks(); track item.id) {
                    <div
                      class="tw-absolute tw-top-0.5 tw-h-5 tw-rounded tw-cursor-pointer"
                      [class]="item.colorClass"
                      [style.left.%]="item.leftPct"
                      [style.width.%]="item.widthPct"
                      [title]="taskTooltip(item)"
                      (click)="selectTask(item.id)"
                    >
                      <span class="tw-px-1 tw-truncate tw-block tw-leading-5 tw-text-[10px]">{{ item.label }}</span>
                    </div>
                  }
                </div>

                <!-- PAYMENTS row -->
                <div class="tw-relative tw-h-6 tw-mb-1">
                  <span class="tw-absolute tw-left-0 tw--translate-x-full tw-pr-2 tw-text-gray-500 tw-text-[10px] tw-leading-6">PAYMENTS</span>
                  @for (item of timelinePayments(); track item.id) {
                    <div
                      class="tw-absolute tw-top-1.5 tw-w-3 tw-h-3 tw-rounded-full tw-border-2 tw-cursor-pointer"
                      [class]="item.colorClass"
                      [style.left.%]="item.leftPct"
                      [title]="paymentTooltip(item)"
                    ></div>
                  }
                </div>

                <!-- Legend -->
                <div class="tw-flex tw-gap-3 tw-text-[10px] tw-text-gray-500 tw-mt-2">
                  <span><span class="tw-inline-block tw-w-3 tw-h-3 tw-rounded-full tw-bg-success-500/70 tw-border tw-border-success-500 tw-align-middle"></span> payment OK</span>
                  <span><span class="tw-inline-block tw-w-3 tw-h-3 tw-rounded-full tw-bg-error-500/70 tw-border tw-border-error-500 tw-align-middle"></span> payment failed</span>
                  <span><span class="tw-inline-block tw-w-3 tw-h-3 tw-rounded-full tw-bg-warning-500/70 tw-border tw-border-warning-500 tw-align-middle"></span> payment pending/processing</span>
                  <span><span class="tw-inline-block tw-w-3 tw-h-2 tw-bg-info-500/40 tw-border tw-border-info-500 tw-align-middle"></span> trigger task</span>
                  <span><span class="tw-inline-block tw-w-3 tw-h-2 tw-bg-gray-600/40 tw-border tw-border-gray-500 tw-align-middle"></span> other task</span>
                </div>
              </div>
            </div>
          </section>

          <!-- Section 2 : Matching Table -->
          <section class="tw-border tw-border-gray-800 tw-rounded tw-bg-gray-900/40">
            <header class="tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-gray-800">
              <span class="ph ph-table tw-text-success-500"></span>
              <span class="tw-font-semibold">Payment matching table</span>
              <span class="tw-text-gray-500 tw-text-[10px]">{{ matchRows().length }} rows</span>
            </header>
            <div class="tw-overflow-x-auto">
              <table class="tw-w-full tw-text-[10px]">
                <thead class="tw-text-gray-400 tw-bg-gray-800/40">
                  <tr>
                    <th class="tw-text-left tw-px-2 tw-py-1">Task name</th>
                    <th class="tw-text-left tw-px-2 tw-py-1">createdAt</th>
                    <th class="tw-text-center tw-px-2 tw-py-1">Trigger?</th>
                    <th class="tw-text-left tw-px-2 tw-py-1">Matched Payment</th>
                    <th class="tw-text-right tw-px-2 tw-py-1">Δt</th>
                    <th class="tw-text-center tw-px-2 tw-py-1">Enrich?</th>
                    <th class="tw-text-center tw-px-2 tw-py-1">Synth?</th>
                    <th class="tw-text-right tw-px-2 tw-py-1">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of matchRows(); track row.taskId) {
                    <tr
                      class="tw-border-t tw-border-gray-800/60 hover:tw-bg-gray-800/40 tw-cursor-pointer"
                      [ngClass]="row.taskId === selectedTaskId() ? 'tw-bg-gray-800' : ''"
                      (click)="selectTask(row.taskId)"
                    >
                      <td class="tw-px-2 tw-py-1 tw-truncate tw-max-w-[180px]" [title]="row.taskName">{{ row.taskName }}</td>
                      <td class="tw-px-2 tw-py-1 tw-text-gray-400">{{ fmtTime(row.createdAt) }}</td>
                      <td class="tw-px-2 tw-py-1 tw-text-center">
                        @if (row.isTrigger) {
                          <span class="tw-text-success-500">✓</span>
                        } @else {
                          <span class="tw-text-gray-600">—</span>
                        }
                      </td>
                      <td class="tw-px-2 tw-py-1 tw-font-mono tw-text-[10px]">
                        @if (row.matchedPaymentId) {
                          <span class="tw-text-info-500">{{ row.matchedPaymentId }}</span>
                        } @else {
                          <span class="tw-text-gray-600">—</span>
                        }
                      </td>
                      <td class="tw-px-2 tw-py-1 tw-text-right tw-font-mono" [class]="row.deltaClass">
                        {{ fmtDelta(row.deltaMs) }}
                      </td>
                      <td class="tw-px-2 tw-py-1 tw-text-center">
                        @if (row.enrichment) { <span class="tw-text-success-500">✓</span> } @else { <span class="tw-text-gray-600">—</span> }
                      </td>
                      <td class="tw-px-2 tw-py-1 tw-text-center">
                        @if (row.synthetic) { <span class="tw-text-gov-secondary">✓</span> } @else { <span class="tw-text-gray-600">—</span> }
                      </td>
                      <td class="tw-px-2 tw-py-1 tw-text-right tw-font-mono">
                        {{ row.resolvedAmount }} {{ row.currency }}
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </section>

          <!-- Section 3 : Cross-matches -->
          <section class="tw-border tw-rounded"
            [ngClass]="crossMatches().length > 0 ? 'tw-border-error-500 tw-bg-error-500/10' : 'tw-border-gray-800 tw-bg-gray-900'"
          >
            <header class="tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-border-b"
              [ngClass]="crossMatches().length > 0 ? 'tw-border-error-500' : 'tw-border-gray-800'"
            >
              <span class="ph ph-warning-octagon"
                [ngClass]="crossMatches().length > 0 ? 'tw-text-error-500' : 'tw-text-gray-500'"
              ></span>
              <span class="tw-font-semibold">Suspected cross-matches</span>
              <span class="tw-text-gray-400 tw-text-[10px]">{{ crossMatches().length }} risque(s)</span>
            </header>
            <div class="tw-p-2 tw-space-y-2">
              @if (crossMatches().length === 0) {
                <div class="tw-text-gray-500 tw-text-[10px]">Aucun payment rapproché (&lt; 120s) détecté — matching robuste sur ce jeu.</div>
              } @else {
                @for (cm of crossMatches(); track cm.p1.id! + ':' + cm.p2.id!) {
                  <div class="tw-text-[10px] tw-border tw-border-error-500/60 tw-rounded tw-p-2 tw-bg-error-500/10">
                    <div class="tw-text-error-500 tw-font-semibold">
                      ⚠ RISQUE DE MATCHING CROISÉ
                    </div>
                    <div class="tw-text-gray-300 tw-mt-1">
                      Payment <span class="tw-font-mono tw-text-info-500">{{ pidShort(cm.p1) }}</span>
                      ({{ fmtTime(parseTime(cm.p1.createdAt)) }})
                      et <span class="tw-font-mono tw-text-info-500">{{ pidShort(cm.p2) }}</span>
                      ({{ fmtTime(parseTime(cm.p2.createdAt)) }})
                      espacés de <span class="tw-font-mono tw-text-warning-500">{{ Math.round(cm.spanMs / 1000) }}s</span>.
                    </div>
                    @if (cm.suspiciousTasks.length > 0) {
                      <div class="tw-text-gray-400 tw-mt-1">
                        → Tâche(s) potentiellement ambiguë(s) (dans la tolérance ±60s des deux) :
                        <ul class="tw-list-disc tw-list-inside">
                          @for (t of cm.suspiciousTasks; track t.id) {
                            <li>
                              <span class="tw-font-mono">{{ t.id }}</span>
                              — {{ t.name }} à {{ fmtTime(t.at) }}
                            </li>
                          }
                        </ul>
                      </div>
                    } @else {
                      <div class="tw-text-gray-500 tw-mt-1">→ Aucune tâche trigger dans la zone ambiguë (pour l’instant).</div>
                    }
                  </div>
                }
              }
            </div>
          </section>

          <!-- Section 4 : Amount resolution trace -->
          <section class="tw-border tw-border-gray-800 tw-rounded tw-bg-gray-900/40">
            <header class="tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-gray-800">
              <span class="ph ph-currency-circle-dollar tw-text-success-500"></span>
              <span class="tw-font-semibold">Amount resolution trace</span>
              @if (selectedTaskId()) {
                <span class="tw-text-gray-500 tw-text-[10px]">task {{ selectedTaskId() }}</span>
              } @else {
                <span class="tw-text-gray-500 tw-text-[10px]">— sélectionne une task —</span>
              }
            </header>
            <div class="tw-p-2 tw-text-[10px]">
              @if (selectedAmountTrace(); as tr) {
                <ol class="tw-space-y-1 tw-font-mono">
                  @for (step of tr.steps; track step.label; let i = $index) {
                    <li class="tw-flex tw-items-center tw-gap-2">
                      <span class="tw-text-gray-500 tw-w-4 tw-text-right">{{ i + 1 }}.</span>
                      <span [ngClass]="step.skipped ? 'tw-text-gray-500' : 'tw-text-gray-200'" class="tw-flex-1">
                        {{ step.label }}
                      </span>
                      <span class="tw-text-gray-400">→</span>
                      <span
                        [ngClass]="step.picked ? 'tw-text-success-500' : 'tw-text-gray-500'"
                        class="tw-w-40 tw-truncate"
                      >
                        {{ step.value }}
                        @if (step.picked) { <span class="tw-ml-1">✓</span> }
                        @if (step.skipped) { <span class="tw-ml-1">(skipped)</span> }
                      </span>
                    </li>
                  }
                </ol>
                <div class="tw-mt-2 tw-text-gray-200">
                  <span class="tw-font-semibold">Resolved:</span>
                  <span class="tw-text-success-500 tw-font-mono">{{ tr.amount }} FCFA</span>
                  <span class="tw-text-gray-500">(src: {{ tr.source }})</span>
                </div>
              } @else {
                <div class="tw-text-gray-500">Clique sur une ligne du tableau ou une barre de la timeline.</div>
              }
            </div>
          </section>

          <!-- Section 5 : Synthetic tasks inventory -->
          <section class="tw-border tw-border-gray-800 tw-rounded tw-bg-gray-900/40">
            <header class="tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-gray-800">
              <span class="ph ph-flask tw-text-success-500"></span>
              <span class="tw-font-semibold">Synthetic tasks inventory</span>
              <span class="tw-text-gray-500 tw-text-[10px]">{{ syntheticInventory().length }}</span>
            </header>
            <div class="tw-p-2 tw-space-y-1 tw-text-[10px]">
              @if (syntheticInventory().length === 0) {
                <div class="tw-text-gray-500">Aucune tâche synthétique ou enrichie dans la démarche courante.</div>
              }
              @for (item of syntheticInventory(); track item.id) {
                <div class="tw-border tw-border-gray-800/60 tw-rounded tw-p-1.5 tw-bg-gray-800/30">
                  <div class="tw-flex tw-gap-2 tw-items-center">
                    <span class="tw-px-1.5 tw-rounded tw-text-[9px]"
                      [ngClass]="item.type === 'synthetic' ? 'tw-bg-gov-secondary/30 tw-text-gov-secondary' : 'tw-bg-success-500/30 tw-text-success-500'"
                    >{{ item.type }}</span>
                    <span class="tw-font-mono tw-text-gray-300">{{ item.id }}</span>
                    <span class="tw-text-gray-400">{{ item.name }}</span>
                  </div>
                  <pre class="tw-mt-1 tw-text-[9px] tw-text-gray-400 tw-whitespace-pre-wrap tw-break-all">{{ item.metaJson }}</pre>
                </div>
              }
            </div>
          </section>

          <!-- Section bonus : Simulate match -->
          <section class="tw-border tw-border-gray-800 tw-rounded tw-bg-gray-900/40">
            <header class="tw-px-2 tw-py-1 tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-gray-800">
              <span class="ph ph-flask-fill tw-text-success-500"></span>
              <span class="tw-font-semibold">Simulate match</span>
            </header>
            <div class="tw-p-2 tw-text-[10px] tw-space-y-2">
              <div class="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <label class="tw-text-gray-400">Timestamp (ISO ou ms)</label>
                <input
                  type="text"
                  [ngModel]="simTimestamp()"
                  (ngModelChange)="simTimestamp.set($event)"
                  placeholder="ex: 2025-04-15T10:00:30Z"
                  class="tw-px-2 tw-py-0.5 tw-bg-gray-800 tw-border tw-border-gray-700 tw-rounded tw-text-gray-200 tw-font-mono tw-w-64"
                />
                <button
                  type="button"
                  class="tw-px-2 tw-py-0.5 tw-rounded tw-bg-success-700 hover:tw-bg-success-500 tw-text-white"
                  (click)="runSimulation()"
                >Simuler</button>
              </div>
              @if (simResult(); as sr) {
                <div class="tw-font-mono tw-text-gray-300">
                  t = <span class="tw-text-warning-500">{{ fmtDate(sr.t) }}</span>
                  → tasks dans tolérance ±60s : <span class="tw-text-success-500">{{ sr.matched.length }}</span>
                </div>
                <ul class="tw-list-disc tw-list-inside tw-text-gray-400">
                  @for (m of sr.matched; track m.id) {
                    <li><span class="tw-font-mono">{{ m.id }}</span> — {{ m.name }} à {{ fmtTime(m.at) }} (Δ {{ fmtDelta(m.delta) }})</li>
                  }
                </ul>
              }
            </div>
          </section>
        </div>
      }
    </div>
  `,
})
export class DevToolsPaymentTabComponent {
  private readonly dev = inject(DevToolsService);

  // Expose helpers au template
  readonly Math = Math;
  readonly parseTime = parseTime;
  readonly fmtTime = fmtTime;
  readonly fmtDate = fmtDate;
  readonly fmtDelta = fmtDelta;

  // UI state
  readonly hideNonTrigger = signal<boolean>(false);
  readonly zoom = signal<number>(1);
  readonly selectedTaskId = signal<string | null>(null);
  readonly simTimestamp = signal<string>('');
  readonly simResult = signal<{ t: number; matched: Array<{ id: string; name: string; at: number; delta: number }> } | null>(null);

  // Snapshot réactif (refresh tick)
  private readonly snapshot = computed(() => {
    // dépendance sur refreshTick
    this.dev.refreshTick();
    return this.dev.snapshot();
  });

  readonly tasks = computed<TaskRecordDto[]>(() => {
    const raw = (this.snapshot().tasks || []) as TaskRecordDto[];
    return Array.isArray(raw) ? raw : [];
  });

  readonly payments = computed<AnyPayment[]>(() => {
    const extras = this.snapshot().extras || {};
    // priorité aux rawPayments s'ils sont exposés, sinon payments du snapshot
    const raw = (extras['rawPayments'] as AnyPayment[] | undefined)
      ?? (this.snapshot().payments as AnyPayment[] | undefined)
      ?? [];
    return Array.isArray(raw) ? raw : [];
  });

  readonly demarche = computed<any>(() => this.snapshot().demarche);
  readonly demarcheRef = computed<string>(() => {
    const d: any = this.demarche();
    return String(d?.reference || d?.ref || d?._id || '');
  });
  readonly demarcheAmount = computed<number>(() => {
    const d: any = this.demarche();
    const n = parseFloat(String(d?.amount ?? ''));
    return isNaN(n) ? 0 : n;
  });

  readonly hasData = computed(() => this.tasks().length > 0 || this.payments().length > 0);

  // Mapping task -> payment via la fonction métier (pure)
  private readonly merged = computed<TaskRecordDto[]>(() => {
    try {
      return mergeTasksWithPayments(
        this.tasks(),
        this.payments(),
        this.demarcheRef(),
        this.demarcheAmount()
      );
    } catch {
      return this.tasks();
    }
  });

  /* ------------------- Timeline ------------------- */

  readonly timelineBounds = computed(() => {
    const tasks = this.tasks();
    const payments = this.payments();
    const times: number[] = [];
    for (const t of tasks) {
      const start = parseTime(t.createdAt);
      const end = parseTime((t as any).updatedAt) || start;
      if (start) times.push(start);
      if (end) times.push(end);
    }
    for (const p of payments) {
      const t = parseTime(p.createdAt);
      if (t) times.push(t);
    }
    if (times.length === 0) return { start: 0, end: 0, span: 0 };
    const start = Math.min(...times);
    const end = Math.max(...times);
    const span = Math.max(end - start, 1000);
    return { start, end: start + span, span };
  });

  readonly timelineStart = computed(() => this.timelineBounds().start);
  readonly timelineEnd = computed(() => this.timelineBounds().end);

  readonly timeTicks = computed(() => {
    const span = this.timelineBounds().span;
    const start = this.timelineBounds().start;
    if (!span) return [] as Array<{ pct: number; label: string }>;
    const ticks: Array<{ pct: number; label: string }> = [];
    for (let i = 0; i <= 4; i++) {
      const pct = (i / 4) * 100;
      const t = start + (span * i) / 4;
      ticks.push({ pct, label: fmtTime(t) });
    }
    return ticks;
  });

  readonly timelineTasks = computed<TimelineItem[]>(() => {
    const { start, span } = this.timelineBounds();
    if (!span) return [];
    const hide = this.hideNonTrigger();
    return this.tasks()
      .filter((t) => (hide ? isPaymentTriggerTask(t) : true))
      .map((t) => {
        const s = parseTime(t.createdAt);
        const e = parseTime((t as any).updatedAt) || s;
        const leftPct = ((s - start) / span) * 100;
        const widthPct = Math.max(((e - s) / span) * 100, 1.2);
        const trigger = isPaymentTriggerTask(t);
        const isSynthetic = !!(t.data?.['__syntheticPayment']);
        const isEnriched = !!(t.data?.['__paymentEnrichment']);
        let colorClass = 'tw-bg-gray-600/40 tw-border tw-border-gray-500 tw-text-gray-200';
        if (isSynthetic) colorClass = 'tw-bg-gov-secondary/40 tw-border tw-border-gov-secondary tw-text-gray-100';
        else if (isEnriched) colorClass = 'tw-bg-success-500/40 tw-border tw-border-success-500 tw-text-gray-100';
        else if (trigger) colorClass = 'tw-bg-info-500/40 tw-border tw-border-info-500 tw-text-gray-100';
        return {
          kind: 'task' as const,
          id: t._id || t.taskId || '',
          label: t.name || t.label || '(unnamed)',
          start: s,
          end: e,
          leftPct,
          widthPct,
          status: t.status,
          isTrigger: trigger,
          matchedWith: null,
          raw: t,
          colorClass,
        };
      });
  });

  readonly timelinePayments = computed<TimelineItem[]>(() => {
    const { start, span } = this.timelineBounds();
    if (!span) return [];
    return this.payments().map((p) => {
      const s = parseTime(p.createdAt);
      const leftPct = ((s - start) / span) * 100;
      const status = (p.status || '').toUpperCase();
      let colorClass = 'tw-bg-warning-500/70 tw-border tw-border-warning-500';
      if (status === 'SUCCEEDED' || status === 'REFUNDED')
        colorClass = 'tw-bg-success-500/70 tw-border tw-border-success-500';
      else if (status === 'FAILED' || status === 'EXPIRED' || status === 'CANCELLED')
        colorClass = 'tw-bg-error-500/70 tw-border tw-border-error-500';
      return {
        kind: 'payment' as const,
        id: pid(p),
        label: pid(p).slice(-6),
        start: s,
        end: s,
        leftPct,
        widthPct: 0,
        status: p.status,
        raw: p,
        colorClass,
      };
    });
  });

  taskTooltip(item: TimelineItem): string {
    const t = item.raw as TaskRecordDto;
    return [
      `Task: ${t.name || ''}`,
      `id: ${t._id}`,
      `status: ${t.status}`,
      `handlerType: ${(t as any).handlerType || '—'}`,
      `createdAt: ${fmtDate(item.start)}`,
      `updatedAt: ${fmtDate(item.end)}`,
      `trigger: ${item.isTrigger ? 'yes' : 'no'}`,
    ].join('\n');
  }

  paymentTooltip(item: TimelineItem): string {
    const p = item.raw as AnyPayment;
    return [
      `Payment: ${pid(p)}`,
      `status: ${p.status}`,
      `amount: ${p.amount} ${p.currency || 'FCFA'}`,
      `provider: ${p.providerName || p.gateway || '—'}`,
      `createdAt: ${fmtDate(item.start)}`,
    ].join('\n');
  }

  pidShort(p: AnyPayment): string {
    const full = pid(p);
    return full.length > 12 ? '…' + full.slice(-10) : full;
  }

  /* ------------------- Match rows ------------------- */

  readonly matchRows = computed<MatchRow[]>(() => {
    const tasks = this.tasks();
    const payments = this.payments();
    const demarcheAmt = this.demarcheAmount();
    const hide = this.hideNonTrigger();

    // Reproduit la logique d'attribution 1:1 via findMatchingPayment (usedPaymentIds mute)
    const used = new Set<string>();
    const rows: MatchRow[] = [];

    for (const t of tasks) {
      const trigger = isPaymentTriggerTask(t);
      if (hide && !trigger) continue;

      const enrichmentMeta: any = t.data?.['__paymentEnrichment'];
      const syntheticMeta: any = t.data?.['__syntheticPayment'];
      let matched: AnyPayment | null = null;

      if (trigger) {
        matched = findMatchingPayment(t, payments, used);
      }

      const taskTime = parseTime(t.createdAt);
      const payTime = matched ? parseTime(matched.createdAt) : null;
      const delta = matched && payTime ? payTime - taskTime : null;

      const { amount, source } = resolveAmount(t, demarcheAmt);

      rows.push({
        taskId: t._id || t.taskId || '',
        taskName: t.name || t.label || '(unnamed)',
        createdAt: taskTime,
        isTrigger: trigger,
        matchedPaymentId: matched ? pid(matched) : (enrichmentMeta?.paymentId || syntheticMeta?.paymentId || null),
        matchedPaymentTime: payTime,
        deltaMs: delta,
        deltaClass: deltaClassFor(delta),
        enrichment: !!enrichmentMeta,
        synthetic: !!syntheticMeta,
        resolvedAmount: amount,
        amountSource: source,
        currency: (matched?.currency || enrichmentMeta?.paymentCurrency || syntheticMeta?.paymentCurrency || 'FCFA'),
        raw: t,
      });
    }
    return rows;
  });

  /* ------------------- Cross-match detection ------------------- */

  readonly crossMatches = computed<CrossMatch[]>(() => {
    const payments = [...this.payments()]
      .map((p) => ({ ...p, _t: parseTime(p.createdAt) }))
      .filter((p) => p._t > 0)
      .sort((a, b) => a._t - b._t);

    const cross: CrossMatch[] = [];
    const tasks = this.tasks().filter(isPaymentTriggerTask);

    for (let i = 0; i < payments.length - 1; i++) {
      const p1 = payments[i];
      const p2 = payments[i + 1];
      const span = p2._t - p1._t;
      if (span > 0 && span < 120_000) {
        // Tâches qui seraient dans la tolérance ±60s des DEUX
        const ambiguous = tasks
          .map((t) => ({ t, at: parseTime(t.createdAt) }))
          .filter(({ at }) => {
            const d1 = Math.abs(at - p1._t);
            const d2 = Math.abs(at - p2._t);
            return d1 <= 60_000 && d2 <= 60_000;
          })
          .map(({ t, at }) => ({
            id: t._id || t.taskId || '',
            name: t.name || t.label || '(unnamed)',
            at,
          }));

        cross.push({ p1, p2, spanMs: span, suspiciousTasks: ambiguous });
      }
    }
    return cross;
  });

  /* ------------------- Amount trace (selected) ------------------- */

  readonly selectedAmountTrace = computed(() => {
    const id = this.selectedTaskId();
    if (!id) return null;
    const task = this.tasks().find((t) => (t._id || t.taskId) === id);
    if (!task) return null;
    return resolveAmount(task, this.demarcheAmount());
  });

  /* ------------------- Synthetic inventory ------------------- */

  readonly syntheticInventory = computed(() => {
    const out: Array<{ id: string; name: string; type: 'synthetic' | 'enrichment'; metaJson: string }> = [];
    for (const t of this.tasks()) {
      const syn = t.data?.['__syntheticPayment'];
      const enr = t.data?.['__paymentEnrichment'];
      if (syn) {
        out.push({
          id: t._id || t.taskId || '',
          name: t.name || '',
          type: 'synthetic',
          metaJson: safeJson(syn),
        });
      } else if (enr) {
        out.push({
          id: t._id || t.taskId || '',
          name: t.name || '',
          type: 'enrichment',
          metaJson: safeJson(enr),
        });
      }
    }
    return out;
  });

  /* ------------------- Actions ------------------- */

  selectTask(id: string): void {
    this.selectedTaskId.set(id);
  }

  refresh(): void {
    this.dev.refreshTick.update((v) => v + 1);
  }

  runSimulation(): void {
    const raw = this.simTimestamp().trim();
    if (!raw) {
      this.simResult.set(null);
      return;
    }
    let t = parseFloat(raw);
    if (isNaN(t)) t = new Date(raw).getTime();
    if (!Number.isFinite(t) || t <= 0) {
      this.simResult.set({ t: 0, matched: [] });
      return;
    }
    const tolerance = 60_000;
    const matched = this.tasks()
      .filter(isPaymentTriggerTask)
      .map((task) => {
        const at = parseTime(task.createdAt);
        return {
          id: task._id || task.taskId || '',
          name: task.name || '',
          at,
          delta: t - at,
        };
      })
      .filter((m) => Math.abs(m.delta) <= tolerance)
      .sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta));
    this.simResult.set({ t, matched });
  }

  exportCsv(): void {
    const rows = this.matchRows();
    const header = [
      'taskId',
      'taskName',
      'createdAt',
      'isTrigger',
      'matchedPaymentId',
      'matchedPaymentTime',
      'deltaMs',
      'enrichment',
      'synthetic',
      'resolvedAmount',
      'amountSource',
      'currency',
    ];
    const lines = [header.join(',')];
    for (const r of rows) {
      lines.push(
        [
          r.taskId,
          csvEsc(r.taskName),
          r.createdAt ? new Date(r.createdAt).toISOString() : '',
          r.isTrigger,
          r.matchedPaymentId || '',
          r.matchedPaymentTime ? new Date(r.matchedPaymentTime).toISOString() : '',
          r.deltaMs ?? '',
          r.enrichment,
          r.synthetic,
          r.resolvedAmount,
          csvEsc(r.amountSource),
          r.currency,
        ].join(',')
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-matching-${this.demarcheRef() || 'demarche'}-${Date.now()}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

function safeJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return '[unserializable]';
  }
}

function csvEsc(v: unknown): string {
  const s = String(v ?? '');
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
